import type { BoardDto, BoardShareDto, BoardType, TargetInput } from '../../../api/boards';
import type { TargetRow } from '../../BoardsPage/TargetPicker';

/**
 * 게시판 설정 폼의 값 — 시안 v1 `axi-board-settings` (U5 · 2026-09-15).
 *
 * ★ 저장 규칙은 현행 `BoardSettingsScreen` 과 같다 —
 *   · **변경분만 PATCH**(바뀐 게 없으면 보내지 않는다 · 서버가 빈 patch 를 400 으로 막는다).
 *   · 공유 · 알림 대상은 목록 **통째로** PUT(서버가 지우고 다시 넣는다).
 *   · 말머리 · 운영자는 누르는 즉시 개별 API(저장 버튼과 따로).
 * ★ 알림 사용을 꺼도 대상의 알림 체크는 **지우지 않는다**(규칙 3 — 다시 켜면 되살아난다).
 */

/**
 * 입력 길이 상한 — **서버가 정본이다**(`apps/chat-server/src/modules/board/boards-helpers.ts`).
 *
 * ⚠ 이 셋은 화면에 **맨숫자로 박혀 있었다**(`BoardSettingsV1Screen.tsx` 의 `maxLength={100}` ·
 *   `{500}` · `{30}`) — 주석조차 없었다. 이름을 주고 **시험으로 서버와 묶는다**
 *   (`components/editor/content-length.server-contract.test.ts`).
 *
 * ★ 왜 묶나: 바로 옆에서 실제로 겪었다. `write-model.ts` 의 본문 상한에는 「서버
 *   `POST_CONTENT_MAX`」라는 **주석이 있었는데도** 서버가 10,000 → 50,000 으로 오를 때
 *   따라오지 않았다. 주석은 지켜지지 않고, 시험은 지켜진다.
 *   ⇒ 값이 지금 같더라도 시험은 「아무것도 안 잡는」 것이 아니다 — **서버가 바꾸는 날 잡는다.**
 */
export const BOARD_NAME_MAX = 100;    // 서버 BOARD_NAME_MAX
export const BOARD_DESC_MAX = 500;    // 서버 BOARD_DESC_MAX
export const PREFIX_LABEL_MAX = 30;   // 서버 PREFIX_LABEL_MAX

export interface SettingsForm {
  name: string;
  description: string;
  boardType: BoardType;
  isPrivate: boolean;
  allowAnonymous: boolean;
  showMailButton: boolean;
  showSecretTitles: boolean;
  usePostReaders: boolean;
  allowComments: boolean;
  allowPostWrite: boolean;
  notifyEnabled: boolean;
  notifyAllByDefault: boolean;
}

export function formFromBoard(b: BoardDto): SettingsForm {
  return {
    name: b.name,
    description: b.description ?? '',
    boardType: b.boardType,
    isPrivate: b.isPrivate,
    allowAnonymous: b.allowAnonymous,
    showMailButton: b.showMailButton,
    showSecretTitles: b.showSecretTitles,
    usePostReaders: b.usePostReaders,
    allowComments: b.allowComments,
    allowPostWrite: b.allowPostWrite !== false,
    notifyEnabled: b.notifyEnabled,
    notifyAllByDefault: b.notifyAllByDefault,
  };
}

/** 화면에 드러내는 순서 — 「바뀐 항목」 목록도 이 순서로 적는다. */
export const SETTINGS_FIELDS = [
  'name', 'description', 'boardType',
  'isPrivate',
  'allowPostWrite', 'allowAnonymous', 'allowComments', 'showMailButton', 'showSecretTitles', 'usePostReaders',
  'notifyEnabled', 'notifyAllByDefault',
] as const;
export type SettingsField = (typeof SETTINGS_FIELDS)[number];

/** 서버로 보낼 변경분 — 제목은 앞뒤 공백을 떼고, 빈 설명은 null. */
export function buildBoardPatch(board: BoardDto, form: SettingsForm): Partial<SettingsForm & { description: string | null }> {
  const base = formFromBoard(board);
  const p: Record<string, unknown> = {};
  if (form.name.trim() !== board.name) p.name = form.name.trim();
  if ((form.description || null) !== board.description) p.description = form.description || null;
  for (const key of SETTINGS_FIELDS) {
    if (key === 'name' || key === 'description') continue;
    if (form[key] !== base[key]) p[key] = form[key];
  }
  return p as Partial<SettingsForm & { description: string | null }>;
}

export function shareRowsFromDto(shares: readonly BoardShareDto[]): TargetRow[] {
  return shares.map((s) => ({
    targetType: s.targetType,
    targetId: s.targetId,
    canRead: s.canRead,
    canNotify: s.canNotify,
    canWrite: s.canWrite ?? false,
  }));
}

export const shareKey = (t: TargetRow) => `${t.targetType}:${t.targetType === 'company' ? '0' : (t.targetId ?? '0')}`;

/** 대상 목록의 「바뀌었나」 지문 — 순서가 달라도 같은 것으로 본다(현행과 같음). */
export function sharesFingerprint(rows: readonly TargetRow[]): string {
  return JSON.stringify(
    rows
      .map((r) => `${shareKey(r)}:${r.canRead ?? true}:${r.canNotify ?? false}:${r.canWrite ?? false}`)
      .sort(),
  );
}

/** PUT 으로 보낼 목록 — 현행과 같은 기본값(읽기 켬 · 알림 · 작성 끔). */
export function toShareInputs(rows: readonly TargetRow[]): TargetInput[] {
  return rows.map((t) => ({
    targetType: t.targetType,
    targetId: t.targetId ?? '0',
    canRead: t.canRead ?? true,
    canNotify: t.canNotify ?? false,
    canWrite: t.canWrite ?? false,
  }));
}

/** 「바뀐 항목」 — 폼 칸 + 공유 목록(한 항목). */
export function changedSettings(board: BoardDto, form: SettingsForm, sharesDirty: boolean): Array<SettingsField | 'shares'> {
  const patch = buildBoardPatch(board, form);
  const out: Array<SettingsField | 'shares'> = SETTINGS_FIELDS.filter((k) => k in patch);
  if (sharesDirty) out.push('shares');
  return out;
}

export const notifyTargetCount = (rows: readonly TargetRow[]) => rows.filter((r) => r.canNotify).length;
